/** Guest journey → Pre-check-in (plan §2 S5): everything the old Pre-check-in tab showed,
 *  on the draft — and the single owner of `use_operator_pre_check_settings`.
 *
 *  - "Use operator pre-check settings" first. While it is on (the stored value in view
 *    mode, the draft's in edit mode) the tour's own settings are kept but not used, and
 *    are not shown — the dashboard's rule. A note links to the operator's Pre-check-in tab
 *    (with `console_top_view`), and the operator's settings the tour gets are shown under
 *    it, read-only: the tour's effective `pre_check_settings`, which are the operator's
 *    while the STORED switch is on (switched on in the draft only, they show once saved).
 *  - Flow switches, intro and confirmation texts.
 *  - Lead booker / party members: the field list (rename, required, reorder, remove, add
 *    a standard field, one of the operator's custom fields — listed with
 *    `console_top_view`, from the operator's pre-check-in GET — or a new custom field; the
 *    lead booker's first name and email are mandatory), and the tag-category questions (a
 *    switch and the categories asked). "Same fields for party members" makes the party's
 *    list follow the lead booker's.
 *  - Waivers: the switch and the operator's enabled waivers to sign.
 *
 *  Each control is one undo step over every path it moves (`precheck.ts`); the questions
 *  and the field list stay one value to the operator (paired reverts in the config). The
 *  names of categories and waivers come from the operator's lists, each with its failure
 *  said and a Retry. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Switch, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as toursApi from '@/api/tours';
import { FieldsList, type FieldsEdit } from '@/features/precheck/editor/FieldsList';
import { QuestionsList } from '@/features/precheck/editor/QuestionsList';
import { categoryCode, categoryNameOf, type OptionSource, type PrecheckTenant } from '@/features/precheck/editor/sources';
import { WaiverList } from '@/features/precheck/editor/WaiverList';
import { useDraft, useDraftField } from '@/features/records/draft/DraftProvider';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { RecordWidgetProps } from '@/features/records/editor/types';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';
import { BlockRule } from '@/features/records/panels/BlockRule';
import { FactGrid, type Fact } from '@/features/records/panels/FactGrid';
import { HEADING_ROW_PX } from '@/features/records/panels/headingRow';
import { operatorPreCheckInHref } from '@/features/tour-operators/tenantLinks';
import { usePermissions } from '@/hooks/usePermissions';

import { PRECHECK, PRECHECK_DEFAULTS, precheckFieldsCatalog, TOUR_ENTITY } from '../catalog';
import { APPLY_SAME_PATH, applySameEdit, precheckEdit, SCOPE_PATHS, scopeState, splitFields, type PreCheckScope } from '../precheck';
import { useOperatorCustomFields, useTagCategoryOptions, useWaiverOptions } from '../queries';
import { useTourRecord } from '../TourRecordContext';

import { BlockAnchor } from './BlockAnchor';
import { TourFieldGrid } from './TourSectionPanel';

const P = PRECHECK;

// ---------------------------------------------------------------------------
// Shared: the values a pre-check edit reads, one SET per edit
// ---------------------------------------------------------------------------

function usePrecheckDraft(rec: string) {
  const { state, dispatch, baselineOf } = useDraft();
  const valueOf = (path: string) => {
    const e = state.entries[rec]?.[path];
    return e ? e.to : baselineOf(rec, path);
  };
  const apply = (values: Record<string, unknown>, label: string) =>
    dispatch({
      type: 'SET',
      changes: Object.entries(values).map(([path, to]) => ({ rec, path, from: baselineOf(rec, path), to })),
      label,
    });
  return { valueOf, apply };
}

/** The tour's operator, as the shared pieces take it. */
function useTenant(): PrecheckTenant | null {
  const { detail } = useTourRecord();
  return detail.tenant ? { id: detail.tenant.id, code: detail.tenant.code ?? null } : null;
}

/** The operator's tag categories: the cached whole list names the codes; the picker
 *  searches the server. */
function useCategorySource() {
  const { detail } = useTourRecord();
  const q = useTagCategoryOptions(detail.tenant?.id);
  const source: OptionSource = {
    list: q.data,
    complete: q.isSuccess,
    failed: q.isError,
    retry: () => void q.refetch(),
    search: async (query) => (await toursApi.picker(detail.tenant!.id, 'tag_categories', query)).results,
  };
  const nameOf = useMemo(() => categoryNameOf(q.data), [q.data]);
  return { source, nameOf };
}

/** The operator's enabled waivers: the cached list (the server's cap, 200) names the
 *  stored ids — a missing name is only "not there" when the list is shorter than the cap. */
function useWaiverSource(): OptionSource {
  const { detail } = useTourRecord();
  const q = useWaiverOptions(detail.tenant?.id);
  return {
    list: q.data,
    complete: q.isSuccess && (q.data?.length ?? 0) < 200,
    failed: q.isError,
    retry: () => void q.refetch(),
    search: async (query) => (await toursApi.picker(detail.tenant!.id, 'waivers', query)).results,
  };
}

function scopeOf(key: string): PreCheckScope {
  return key.includes('party') ? 'party' : 'lead';
}

function FieldsView({ value, scope }: { value: unknown; scope: PreCheckScope }) {
  const { detail } = useTourRecord();
  const { baselineOf } = useDraft();
  const follows = scope === 'party' && baselineOf(`${TOUR_ENTITY}:${detail.id}`, APPLY_SAME_PATH) === true;
  return <FieldsList scope={scope} value={value} follows={follows} edit={null} />;
}

function QuestionsView({ scope }: { scope: PreCheckScope }) {
  const { detail } = useTourRecord();
  const { baselineOf } = useDraft();
  const { source } = useCategorySource();
  const tenant = useTenant();
  const rec = `${TOUR_ENTITY}:${detail.id}`;
  const st = scopeState((p) => baselineOf(rec, p), scope);
  const follows = scope === 'party' && baselineOf(rec, APPLY_SAME_PATH) === true;
  return (
    <QuestionsList
      scope={scope}
      enabled={st.enabled}
      selected={st.selected}
      follows={follows}
      invalid={null}
      edit={null}
      categories={source}
      tenant={tenant}
    />
  );
}

function WaiversView({ value }: { value: unknown }) {
  return <WaiverList value={value} edit={null} waivers={useWaiverSource()} tenant={useTenant()} />;
}

// ---------------------------------------------------------------------------
// Widgets (edit mode)
// ---------------------------------------------------------------------------

let renameSession = 0;

/** A scope's field list: rename, required, reorder, remove, add. The questions are listed
 *  at the end, read-only here (the questions control edits them). */
export function PreCheckFieldsWidget(p: RecordWidgetProps) {
  const scope = scopeOf(p.field.key);
  const { errorsFor } = useRecordEditor();
  const { valueOf, apply } = usePrecheckDraft(p.rec);
  const { dispatch, baselineOf } = useDraft();
  const { nameOf } = useCategorySource();
  const { detail } = useTourRecord();
  const definitions = useOperatorCustomFields(detail.tenant?.id);
  const { regular } = splitFields(p.value);
  const follows = scope === 'party' && valueOf(APPLY_SAME_PATH) === true;
  const session = useRef<string | null>(null);

  const edit: FieldsEdit = {
    disabled: p.disabled,
    onRegular: (next, label) => apply(precheckEdit(valueOf, scope, { regular: next }, nameOf), label),
    onRename: (i, name) => {
      const next = regular.map((f, j) => (j === i ? { ...f, name } : f));
      const values = precheckEdit(valueOf, scope, { regular: next }, nameOf);
      dispatch({
        type: 'SET',
        changes: Object.entries(values).map(([path, to]) => ({ rec: p.rec, path, from: baselineOf(p.rec, path), to })),
        ...(session.current ? { coalesce: session.current } : {}),
      });
    },
    onRenameFocus: () => {
      renameSession += 1;
      session.current = `${p.rec}|precheck-rename#${renameSession}`;
    },
    onRenameBlur: () => {
      session.current = null;
    },
  };
  return (
    <FieldsList
      scope={scope}
      value={p.value}
      follows={follows}
      edit={follows ? null : edit}
      {...(definitions ? { definitions } : {})}
      baseline={p.baseline}
      errors={errorsFor(p.rec, p.field.key)}
    />
  );
}

/** A scope's tag-category questions: the switch and the categories asked. */
export function PreCheckQuestionsWidget(p: RecordWidgetProps) {
  const scope = scopeOf(p.field.key);
  const { valueOf, apply } = usePrecheckDraft(p.rec);
  const { state } = useDraft();
  const { setInvalidInput } = useRecordEditor();
  const { source, nameOf } = useCategorySource();
  const tenant = useTenant();
  const st = scopeState(valueOf, scope);
  const follows = scope === 'party' && valueOf(APPLY_SAME_PATH) === true;
  const who = scope === 'lead' ? 'the lead booker' : 'party members';
  // Switched on with nothing to ask: Save is blocked (the dashboard refuses it too) — only
  // when the draft made it so; a tour stored like this saves other changes.
  const paths = SCOPE_PATHS[scope];
  const touched = Boolean(state.entries[p.rec]?.[paths.enabled] || state.entries[p.rec]?.[paths.selected]);
  const invalid = touched && st.enabled && st.selected.length === 0 ? 'Pick at least one category, or switch the questions off' : null;
  useEffect(() => {
    setInvalidInput(p.rec, paths.enabled, invalid);
  }, [invalid, p.rec, paths.enabled, setInvalidInput]);
  useEffect(() => () => setInvalidInput(p.rec, paths.enabled, null), [p.rec, paths.enabled, setInvalidInput]);

  return (
    <QuestionsList
      scope={scope}
      enabled={st.enabled}
      selected={st.selected}
      follows={follows}
      invalid={invalid}
      categories={source}
      tenant={tenant}
      edit={{
        disabled: p.disabled || follows,
        id: p.id,
        describedBy: p.describedBy,
        onEnabled: (on) =>
          apply(precheckEdit(valueOf, scope, { enabled: on }, nameOf), `${on ? 'Ask' : 'Stop asking'} ${who} tag-category questions`),
        onRemove: (code) =>
          apply(
            precheckEdit(valueOf, scope, { selected: st.selected.filter((c) => c !== code) }, nameOf),
            `Stop asking ${who} about ${nameOf(code) ?? code}`,
          ),
        onAdd: (o) => {
          const code = categoryCode(o);
          apply(
            precheckEdit(valueOf, scope, { selected: [...st.selected, code] }, (c) => (c === code ? o.name : nameOf(c))),
            `Ask ${who} about ${o.name ?? code}`,
          );
        },
      }}
    />
  );
}

/** "Same fields for party members": on copies the lead booker's fields and questions. */
export function PreCheckApplySameWidget(p: RecordWidgetProps) {
  const { valueOf, apply } = usePrecheckDraft(p.rec);
  const { nameOf } = useCategorySource();
  const on = p.value === true;
  return (
    <Box
      sx={[{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 40, borderRadius: 1, px: 0.5 }, on !== (p.baseline === true) && { bgcolor: CHANGED_TINT }]}
    >
      <Switch
        size="small"
        checked={on}
        disabled={p.disabled}
        onChange={(e) =>
          apply(
            applySameEdit(valueOf, e.target.checked, nameOf),
            e.target.checked ? 'Party members answer the lead booker’s fields' : 'Party members get their own fields',
          )
        }
        inputProps={{ id: p.id, 'aria-describedby': p.describedBy }}
      />
      <Typography variant="body2" aria-hidden>
        {on ? 'Yes' : 'No'}
      </Typography>
    </Box>
  );
}

/** The waivers guests sign: the operator's enabled waivers, by name. */
export function PreCheckWaiversWidget(p: RecordWidgetProps) {
  const waivers = useWaiverSource();
  const tenant = useTenant();
  return (
    <WaiverList
      value={p.value}
      edit={{ disabled: p.disabled, onChange: (next, label) => p.onChange(next, { label }) }}
      waivers={waivers}
      tenant={tenant}
    />
  );
}

export const PRECHECK_WIDGETS = {
  // A list widget: its rows mark their own changes, errors and questions.
  precheck_fields: Object.assign(PreCheckFieldsWidget, { owns: 'items' as const }),
  precheck_categories: PreCheckQuestionsWidget,
  precheck_apply_same: PreCheckApplySameWidget,
  precheck_waivers: PreCheckWaiversWidget,
};

// ---------------------------------------------------------------------------
// The block
// ---------------------------------------------------------------------------

function Sub({ children }: { children: ReactNode }) {
  return (
    <Typography variant="caption" component="h4" color="text.secondary" sx={{ m: 0, mt: 0.5, fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// The operator's settings, read-only (a tour that uses them)
// ---------------------------------------------------------------------------

/** The settings' labels and help as the operator's form words them (one wording for both
 *  editors; the operator's lists are its own, required flags and order included). */
const OPERATOR_FIELDS = new Map(precheckFieldsCatalog('tour_operator').map((f) => [f.key, f]));
const labelOf = (path: string) => OPERATOR_FIELDS.get(path)?.label ?? path;
const QUESTIONS_LABEL = 'Tag-category questions';

/** The note on a tour that uses the operator's settings, with the way to them. */
function OperatorSettingsNote() {
  const { detail } = useTourRecord();
  const { hasPerm } = usePermissions();
  const topId = detail.tenant?.id;
  return (
    <Box data-precheck-operator sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flex: '1 1 260px', minWidth: 0 }}>
        <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px' }} />
        <span>
          This tour uses the operator’s pre-check-in settings. Its own settings are kept but not used — switch this off to edit
          them.
        </span>
      </Typography>
      {topId != null && hasPerm('console_top_view') && (
        <Button size="small" component={RouterLink} to={operatorPreCheckInHref(topId)} sx={{ py: 0, minHeight: 24 }}>
          Operator’s pre-check-in settings
        </Button>
      )}
    </Box>
  );
}

/** What a tour that uses the operator's settings gets: those settings, read-only, in the
 *  guest's order — the flow switches, the intro, the lead booker's and the party's fields
 *  and questions, the waivers, the confirmation — on the shared pieces in view mode. */
function InheritedSettings({ settings }: { settings: Readonly<Record<string, unknown>> }) {
  const { detail } = useTourRecord();
  const { source } = useCategorySource();
  const waivers = useWaiverSource();
  const tenant = useTenant();
  const valueOf = (path: string) => {
    const k = path.slice(PRECHECK('').length);
    return settings[k] ?? PRECHECK_DEFAULTS[k];
  };
  const flag = (k: string): Fact => {
    const path = PRECHECK(k);
    const help = OPERATOR_FIELDS.get(path)?.help;
    return { key: path, label: labelOf(path), value: valueOf(path) === true ? 'Yes' : 'No', ...(help ? { help } : {}) };
  };
  const text = (k: string): Fact => {
    const path = PRECHECK(k);
    const v = valueOf(path);
    return { key: path, label: labelOf(path), value: typeof v === 'string' ? v : null, wide: true };
  };
  // "Same fields for party members" is a fact of its own here: the party's lists show as
  // stored (a copy of the lead booker's), without the editor's "turn it off to edit" note.
  const scopeFacts = (scope: PreCheckScope): Fact[] => {
    const paths = SCOPE_PATHS[scope];
    const st = scopeState(valueOf, scope);
    return [
      {
        key: paths.fields,
        label: labelOf(paths.fields),
        value: <FieldsList scope={scope} value={valueOf(paths.fields)} follows={false} edit={null} />,
        wide: true,
      },
      {
        key: paths.selected,
        label: QUESTIONS_LABEL,
        value: (
          <QuestionsList
            scope={scope}
            enabled={st.enabled}
            selected={st.selected}
            follows={false}
            invalid={null}
            edit={null}
            categories={source}
            tenant={tenant}
          />
        ),
        wide: true,
      },
    ];
  };
  return (
    <BlockRule title={`From ${detail.tenant?.name ?? 'the operator'}`} note="read-only, the operator’s pre-check-in settings">
      <Box
        data-precheck-inherited
        sx={{
          display: 'grid',
          gap: 0.5,
          // The lists keep an add row's room in view mode so E moves nothing; nothing here is
          // ever edited, so that room would only be a gap.
          '& [data-precheck-add]': { display: 'none' },
        }}
      >
        <FactGrid facts={[flag('enabled'), flag('required'), flag('allow_edit_after_checkin')]} />
        <FactGrid facts={[text('intro_text')]} />
        <Sub>Lead booker</Sub>
        <FactGrid facts={scopeFacts('lead')} />
        <Sub>Party members</Sub>
        <FactGrid facts={[flag('collect_party_members_info'), flag('include_party_members_in_chat'), flag('apply_same_fields_to_party_members')]} />
        <FactGrid facts={scopeFacts('party')} />
        <Sub>Waivers</Sub>
        <FactGrid
          facts={[
            flag('waiver_enabled'),
            {
              key: PRECHECK('waivers'),
              label: labelOf(PRECHECK('waivers')),
              value: (
                <WaiverList
                  value={valueOf(PRECHECK('waivers'))}
                  edit={null}
                  waivers={waivers}
                  tenant={tenant}
                  missingNote="not among the operator’s enabled waivers — guests don’t see it"
                />
              ),
              wide: true,
            },
          ]}
        />
        <Sub>Confirmation</Sub>
        <FactGrid facts={[text('confirmation_message')]} />
      </Box>
    </BlockRule>
  );
}

export function PreCheckBlock() {
  const { detail } = useTourRecord();
  const { mode } = useRecordEditor();
  const rec = `${TOUR_ENTITY}:${detail.id}`;
  const useOperator = useDraftField(TOUR_ENTITY, detail.id, 'use_operator_pre_check_settings');
  const usesOperator = (mode === 'edit' ? useOperator.value : useOperator.baseline) === true;
  // The tour's effective settings are the operator's only while the STORED switch is on.
  const inherited = useOperator.baseline === true && detail.tenant ? detail.pre_check_settings : null;
  const { baselineOf } = useDraft();
  const stored = (path: string) => baselineOf(rec, path);

  return (
    <BlockAnchor id="pre-check-in" label="Pre-check-in">
      <Box sx={{ display: 'grid', gap: 0.5 }}>
        <Box data-block-heading sx={{ display: 'flex', alignItems: 'center', minHeight: HEADING_ROW_PX }}>
          <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
            Pre-check-in
          </Typography>
        </Box>
        <TourFieldGrid keys={['use_operator_pre_check_settings']} />
        {usesOperator ? (
          <>
            <OperatorSettingsNote />
            {inherited ? (
              <InheritedSettings settings={inherited} />
            ) : (
              <Typography variant="caption" color="text.secondary" data-precheck-inherited-pending>
                The operator’s settings show here once this is saved.
              </Typography>
            )}
          </>
        ) : (
          <>
            <TourFieldGrid keys={[P('enabled'), P('required'), P('allow_edit_after_checkin')]} />
            <TourFieldGrid keys={[P('intro_text'), P('confirmation_message')]} />
            <Sub>Lead booker</Sub>
            <TourFieldGrid
              keys={[P('lead_booker_fields'), P('lead_selected_tag_categories')]}
              displays={{
                [P('lead_booker_fields')]: <FieldsView value={stored(P('lead_booker_fields'))} scope="lead" />,
                [P('lead_selected_tag_categories')]: <QuestionsView scope="lead" />,
              }}
              labels={{ [P('lead_selected_tag_categories')]: 'Tag-category questions' }}
            />
            <Sub>Party members</Sub>
            <TourFieldGrid keys={[P('collect_party_members_info'), P('include_party_members_in_chat'), P('apply_same_fields_to_party_members')]} />
            <TourFieldGrid
              keys={[P('party_member_fields'), P('party_selected_tag_categories')]}
              displays={{
                [P('party_member_fields')]: <FieldsView value={stored(P('party_member_fields'))} scope="party" />,
                [P('party_selected_tag_categories')]: <QuestionsView scope="party" />,
              }}
              labels={{ [P('party_selected_tag_categories')]: 'Tag-category questions' }}
            />
            <Sub>Waivers</Sub>
            <TourFieldGrid
              keys={[P('waiver_enabled'), P('waivers')]}
              displays={{ [P('waivers')]: <WaiversView value={stored(P('waivers'))} /> }}
            />
          </>
        )}
      </Box>
    </BlockAnchor>
  );
}
